import { Box, CardMedia, Divider, Icon, Skeleton, Typography } from '@mui/material'
import React from 'react'
import defaultAvatar from 'assets/images/default_avatar.png'
import { useQuery, useQueryClient } from 'react-query'
import { getSimilarityScore } from 'services/api/modules/recruitment/job-application'
import ShowChartIcon from '@mui/icons-material/ShowChart'

const SimilarityScoreDrawer = ({ scoreData, onClose }) => {
  const chartColors = [
    'rgba(245, 210, 22)',
    'rgba(126, 61, 121)',
    'rgba(32, 81, 238)',
    'rgba(255, 92, 0)',
  ]
  const benchmarkTraits = scoreData?.benchmark?.personality_traits

  const queryClient = useQueryClient()
  const {
    isLoading: similarityScoreIsLoading,
    error: similarityScoreError,
    data: similarityScoreData,
    refetch,
  } = useQuery(
    ['getSimilarityScore', scoreData],
    async () => {
      const benchmarkScores = [
        benchmarkTraits?.freedom?.dominant_percentage * 0.01 ||
          benchmarkTraits?.freedom?.average_percentage * 0.01,
        benchmarkTraits?.adventurous?.dominant_percentage * 0.01 ||
          benchmarkTraits?.adventurous?.average_percentage * 0.01,
        benchmarkTraits?.teamwork?.dominant_percentage * 0.01 ||
          benchmarkTraits?.teamwork?.average_percentage * 0.01,
        benchmarkTraits?.systematic?.dominant_percentage * 0.01 ||
          benchmarkTraits?.systematic?.average_percentage * 0.01,
        benchmarkTraits?.ambitious?.dominant_percentage * 0.01 ||
          benchmarkTraits?.ambitious?.average_percentage * 0.01,
      ]

      const comparison = scoreData?.compareArray?.map((item) => ({
        id: item?.id,
        // type 1 for department type 2 for employee
        type: item?.personality_traits?.freedom?.dominant_percentage ? 2 : 1,
        score: [
          item?.personality_traits?.freedom?.dominant_percentage * 0.01 ??
            item?.personality_traits?.freedom?.average_percentage * 0.01,
          item?.personality_traits?.adventurous?.dominant_percentage * 0.01 ??
            item?.personality_traits?.adventurous?.average_percentage * 0.01,
          item?.personality_traits?.teamwork?.dominant_percentage * 0.01 ??
            item?.personality_traits?.teamwork?.average_percentage,
          item?.personality_traits?.systematic?.dominant_percentage * 0.01 ??
            item?.personality_traits?.systematic?.average_percentage * 0.01,
          item?.personality_traits?.ambitious?.dominant_percentage * 0.01 ??
            item?.personality_traits?.ambitious?.average_percentage * 0.01,
        ],
      }))

      const { data } = await getSimilarityScore({
        benchmark: benchmarkScores,
        comparison,
      })

      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          paddingX: '24px',
          paddingY: '16px',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" color="dark-text.main">
            Similarity score ({scoreData?.compareArray.length})
          </Typography>
          <Icon
            sx={{
              fontSize: '21px',
              cursor: 'pointer',
              '&:hover': {
                color: 'error.main',
              },
              color: 'dark-text.main',
            }}
            onClick={() => {
              onClose()
            }}
          >
            clear
          </Icon>
        </Box>
      </Box>
      <Divider></Divider>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '24px' }}>
        <Typography>
          {`Similarity score compared to `}
          <span style={{ fontSize: '14px', fontWeight: 600 }}>{scoreData?.benchmark?.name}</span>
        </Typography>
        {similarityScoreIsLoading ? (
          <Skeleton variant="rounded" height="200px" width="100%" />
        ) : (
          <Box display="flex" flexDirection="column">
            {scoreData?.compareArray.map((option, key) => (
              <Box
                key={key}
                border={1}
                borderColor="gray-line.main"
                borderTop={key === 0 ? 1 : 0}
                borderTopColor="gray-line.main"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  borderTopLeftRadius: key === 0 ? '4.5px' : null,
                  borderTopRightRadius: key === 0 ? '4.5px' : null,
                  borderBottomLeftRadius:
                    key === scoreData?.compareArray.length - 1 ? '4.5px' : null,
                  borderBottomRightRadius:
                    key === scoreData?.compareArray.length - 1 ? '4.5px' : null,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingX: '12px',
                  }}
                  width="56px"
                >
                  <ShowChartIcon
                    sx={{
                      color: chartColors[key % chartColors.length],
                    }}
                  />
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  gap="16px"
                  padding="16px"
                  borderLeft={1}
                  borderRight={1}
                  borderColor="gray-line.main"
                  width="100%"
                >
                  <CardMedia
                    component="img"
                    sx={{
                      height: 45,
                      width: 45,
                      objectFit: 'cover',
                      borderRadius: '100%',
                    }}
                    src={option?.profile_image_url ?? defaultAvatar}
                  />

                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="subtitle1"
                        color="dark-text.main"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {option?.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="light-gray-text.main"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {scoreData?.type === 'Department personality comparison' ? (
                          <>
                            {`${option?.staff_count} ${
                              option?.staff_count === 1 ? 'member' : 'members'
                            }`}
                          </>
                        ) : (
                          option?.email
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box>
                  {similarityScoreData && (
                    <Typography
                      sx={{ textAlign: 'center', padding: '10px', width: '65px' }}
                      key={key}
                    >{`${similarityScoreData[key]?.similarityScore}%`}</Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default SimilarityScoreDrawer
